import React, {useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';
import {BeatLoader} from 'react-spinners';

import {styled} from "@mui/material/styles";

import ListingProductImage from "./ListingProductImage";
import ListingDemandTile from "./ListingDemandTile";
import {ReactComponent as EditIcon} from "assets/img/edit-2-linear.svg";
import {black500, blue500, blue600, grey200, greyishBlue200, greyishBlue500} from "assets/jss/main";

ListingProductImageCell.propTypes = {
    // renders the rank field (products list only)
    showRanking: PropTypes.bool,
    ranking: PropTypes.number,
    // async (ranking|null) => Promise — called on Enter / blur when the value
    // changed; absent = read-only field
    onRankingSave: PropTypes.func,
    src: PropTypes.string,
    alt: PropTypes.string,
    groupedSearchImpression: PropTypes.number,
};

const Container = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    width: '84px'
});

const RankingWrapper = styled('div')({
    position: 'relative'
});

const DerankButton = styled('button')({
    position: 'absolute',
    top: 0,
    left: 'calc(100% + 8px)',
    zIndex: 2,
    height: '40px',
    padding: '0 16px',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: blue500,
    color: '#fff',
    fontSize: '14px',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: blue600
    }
});

const RankingField = styled('div', {shouldForwardProp: (prop) => 'disabled' !== prop})(({disabled}) => ({
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '0 12px',
    backgroundColor: disabled ? grey200 : '#fff',
    border: `1px solid ${disabled ? 'transparent' : greyishBlue200}`,
    borderRadius: '8px',
    cursor: disabled ? 'default' : 'text',
    'svg': {
        width: '16px',
        height: '16px',
        flexShrink: 0
    },
    'svg path': {
        stroke: disabled ? greyishBlue200 : greyishBlue500
    }
}));

const RankingInput = styled('input')({
    flex: 1,
    minWidth: 0,
    padding: 'unset',
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    color: black500,
    fontSize: '14px',
    fontFamily: 'inherit',
    '&:disabled': {
        color: greyishBlue200,
        WebkitTextFillColor: greyishBlue200
    }
});

// integers only, optionally negative, capped at 7 digits
const sanitizeRanking = (raw) => {
    const isNegative = raw.startsWith('-');
    const digits = raw.replace(/\D/g, '').slice(0, 7);

    return `${isNegative ? '-' : ''}${digits}`;
};

// empty (or lone "-") input means "no ranking"
const parseRanking = (value) => {
    const parsed = parseInt(value, 10);

    return Number.isNaN(parsed) ? null : parsed;
};

function ListingProductImageCell({showRanking = false, ranking, onRankingSave, src, alt, groupedSearchImpression}) {
    const rankingInputRef = useRef(null);
    const [rankingValue, setRankingValue] = useState(ranking ?? '');
    const [savedRanking, setSavedRanking] = useState(ranking ?? null);
    const [isSavingRanking, setIsSavingRanking] = useState(false);
    const [isRankingFocused, setIsRankingFocused] = useState(false);
    const isSavingRef = useRef(false);

    // a click anywhere in the field (pencil included) focuses the input
    const handleRankingFieldClick = (event) => {
        event.stopPropagation();
        rankingInputRef.current?.focus();
    };

    const saveRanking = async (newRanking) => {
        // normalize the display ("-" or "007" leftovers)
        setRankingValue(newRanking ?? '');

        if (!onRankingSave || newRanking === savedRanking) {
            return;
        }

        isSavingRef.current = true;
        setIsSavingRanking(true);
        try {
            await onRankingSave(newRanking);
            setSavedRanking(newRanking);
        } catch (error) {
            // revert to the last saved value
            setRankingValue(savedRanking ?? '');
        } finally {
            isSavingRef.current = false;
            setIsSavingRanking(false);
        }
    };

    const handleRankingBlur = () => {
        setIsRankingFocused(false);

        if (isSavingRef.current) {
            return;
        }

        saveRanking(parseRanking(rankingValue));
    };

    const handleDerankMouseDown = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDerank = (event) => {
        event.stopPropagation();
        setIsRankingFocused(false);
        saveRanking(null);
    };

    const handleRankingKeyDown = (event) => {
        if ('Enter' === event.key) {
            // the blur handler does the saving
            event.currentTarget.blur();
        }
    };

    return (
        <Container>
            {showRanking && (
                <RankingWrapper>
                    <RankingField disabled={isSavingRanking} onClick={handleRankingFieldClick}>
                        <RankingInput
                            ref={rankingInputRef}
                            type="text"
                            inputMode="numeric"
                            value={rankingValue}
                            onChange={(event) => setRankingValue(sanitizeRanking(event.target.value))}
                            onFocus={() => setIsRankingFocused(true)}
                            onBlur={handleRankingBlur}
                            onKeyDown={handleRankingKeyDown}
                            readOnly={!onRankingSave}
                            disabled={isSavingRanking}
                        />
                        {isSavingRanking
                            ? <BeatLoader sizeUnit={"px"} size={4} color={greyishBlue500} loading={true}/>
                            : <EditIcon/>}
                    </RankingField>
                    {isRankingFocused && onRankingSave && null !== savedRanking && (
                        <DerankButton type="button" onMouseDown={handleDerankMouseDown} onClick={handleDerank}>
                            <FormattedMessage id={"retailer.product.list.ranking.derank"}/>
                        </DerankButton>
                    )}
                </RankingWrapper>
            )}
            <ListingProductImage src={src} alt={alt}/>
            <ListingDemandTile groupedSearchImpression={groupedSearchImpression}/>
        </Container>
    );
}

export default ListingProductImageCell;
